import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import {
  classTypes,
  familyTypes,
  FilterOperationType,
  MAX_BASE_PLANS_PER_PAGE,
  MAX_ELEVATIONS_PER_BASE_PLAN,
  sortFieldsEnum,
  sortOrderEnum,
} from 'src/lib/constants'
import {
  IEnhancedPlanFilterCriteria,
  IEnhancedUnitFilterCriteria,
  IFilteredPlanDetailsResponse,
  IFilteredPlansRequest,
  IFilteredPlansResponse,
  IFilteredUnitDetailsResponse,
  IFilteredUnitsRequest,
  IFilteredUnitsResponse,
  IGroupedPlans,
  IGroupedUnits,
  IPlan,
  IUnit,
} from 'src/types/filterPlans.interfaces'
import {
  IEnhancedShellFilterCriteria,
  IFilteredShellDetailsResponse,
  IFilteredShellsRequest,
  IFilteredShellsResponse,
  IShell,
} from 'src/types/filterShells.interfaces'
import { compareElevations } from 'src/utils/elevation.util'
import {
  generatePlanFilters,
  generateShellFilters,
  generateUnitFilters,
  hasValidFilterValue,
} from 'src/utils/filterGenerators'
import {
  loadComparisonAttributes,
  loadFilterValues,
  loadVisibleFilters,
  saveComparisonAttributes,
  saveFilterValues,
  saveVisibleFilters,
} from 'src/utils/filterStorage.util'

import { getFilteredPlans } from '../actions/get-filtered-plans'
import {
  getMultiFamilyDetails,
  GetMultiFamilyDetailsParams,
  getPlanDetails,
  GetPlanDetailsParams,
} from '../actions/get-plan-details'
import { RootState } from '../stores/store'
import {
  comparisonAttributeFields,
  ComparisonAttributeItem,
  exportMenuFields,
  filterMenuFields,
  FilterMenuItems,
} from './SearchCompareData'

export interface FilteredPlansState {
  comparisonAttributes: ComparisonAttributeItem[]
  error: string | null
  exportFilters: FilterMenuItems[]
  filters: FilterMenuItems[]
  initialExportFilters: FilterMenuItems[]
  initialFilters: FilterMenuItems[] // Store initial state
  loading: boolean
  plan: IFilteredPlanDetailsResponse
  planRequestDto: IFilteredPlansRequest
  plans: IFilteredPlansResponse
  previousFilters: FilterMenuItems[] // Store previous state
  shell: IFilteredShellDetailsResponse
  shellRequestDto: IFilteredShellsRequest
  shells: IFilteredShellsResponse
  unit: IFilteredUnitDetailsResponse
  unitRequestDto: IFilteredUnitsRequest
  units: IFilteredUnitsResponse
}

const initialPaginationState = {
  currentPage: 1,
  hasNextPage: false,
  hasPreviousPage: false,
  pageSize: MAX_BASE_PLANS_PER_PAGE,
  totalCount: 0,
  totalPages: 1,
}

const initialUnitsState = {
  data: {
    units: {},
  },
  pagination: initialPaginationState,
}

const initialShellsState = {
  data: {
    shells: {},
  },
  pagination: initialPaginationState,
}

const initialPlansState = {
  data: {
    plans: {},
  },
  pagination: initialPaginationState,
}

export const initialState: FilteredPlansState = {
  comparisonAttributes: comparisonAttributeFields, // Will be loaded when class type is initialized
  error: null,
  exportFilters: exportMenuFields,
  filters: filterMenuFields,
  initialExportFilters: exportMenuFields, // Initialize with same export filters
  initialFilters: filterMenuFields, // Initialize with same filters
  loading: false,
  plan: {} as IFilteredPlanDetailsResponse,
  planRequestDto: {
    currentPage: 1,
    filters: generatePlanFilters(),
    pageSize: MAX_BASE_PLANS_PER_PAGE,
    submitted: true,
  } as IFilteredPlansRequest,
  plans: initialPlansState,
  previousFilters: filterMenuFields,
  shell: {} as IFilteredShellDetailsResponse,
  shellRequestDto: {
    currentPage: 1,
    filters: generateShellFilters(),
    pageSize: MAX_BASE_PLANS_PER_PAGE,
    submitted: true,
  } as IFilteredShellsRequest,
  shells: initialShellsState,
  unit: {} as IFilteredUnitDetailsResponse,
  unitRequestDto: {
    currentPage: 1,
    filters: generateUnitFilters(),
    pageSize: MAX_BASE_PLANS_PER_PAGE,
    submitted: true,
  } as IFilteredUnitsRequest,
  units: initialUnitsState,
}

export const getInitialPlanFilters = (): IFilteredPlansRequest['filters'] =>
  cloneDeep(initialState.planRequestDto.filters)

export const getInitialShellFilters = (): IFilteredShellsRequest['filters'] =>
  cloneDeep(initialState.shellRequestDto.filters)

export const getInitialUnitFilters = (): IFilteredUnitsRequest['filters'] =>
  cloneDeep(initialState.unitRequestDto.filters)

export const fetchFilteredPlans = createAsyncThunk(
  '/filteredPlans',
  async (planRequestDto: IFilteredPlansRequest | undefined, { getState }) => {
    const state = getState() as { filteredPlans: FilteredPlansState }
    const { single } = (getState() as RootState).FavoritePlans.favoriteBasePlans

    const finalRequestDto = planRequestDto || state.filteredPlans.planRequestDto

    const filters = Object.fromEntries(
      Object.entries(finalRequestDto?.filters || {}).filter(
        ([, value]) =>
          Object.keys(value).length > 1 && hasValidFilterValue(value)
      )
    )

    const request = { ...finalRequestDto, filters }
    const response = (await getFilteredPlans(request, single))
      ?.data as IFilteredPlansResponse
    const groupedPlans: IGroupedPlans = response?.data as IGroupedPlans

    if (groupedPlans?.plans) {
      const plans = groupedPlans.plans
      Object.keys(plans).forEach((planKey) => {
        plans[planKey] = [...plans[planKey]].sort((a, b) =>
          compareElevations(a.planElevation, b.planElevation)
        )
      })
    }

    return response
  }
)

export const fetchFilteredUnits = createAsyncThunk(
  '/filteredUnits',
  async (unitRequestDto: IFilteredUnitsRequest | undefined, { getState }) => {
    const state = getState() as { filteredPlans: FilteredPlansState }
    const { units } = (getState() as RootState).FavoritePlans.favoriteBasePlans

    const finalRequestDto = unitRequestDto || state.filteredPlans.unitRequestDto

    const filters = Object.fromEntries(
      Object.entries(finalRequestDto?.filters || {}).filter(
        ([, value]) =>
          Object.keys(value).length > 1 && hasValidFilterValue(value)
      )
    )

    const request = { ...finalRequestDto, filters }
    const response = (await getFilteredPlans(request, units))
      ?.data as IFilteredUnitsResponse
    const groupedUnits: IGroupedUnits = response?.data as IGroupedUnits
    if (groupedUnits?.units) {
      const units = groupedUnits.units
      Object.keys(units).forEach((unitKey) => {
        units[unitKey] = [...units[unitKey]].sort((a, b) =>
          compareElevations(a.unitElevation, b.unitElevation)
        )
      })
    }
    return response
  }
)

export const fetchFilteredShells = createAsyncThunk(
  '/filteredShells',
  async (shellRequestDto: IFilteredShellsRequest | undefined, { getState }) => {
    const state = getState() as { filteredPlans: FilteredPlansState }
    const { shells } = (getState() as RootState).FavoritePlans.favoriteBasePlans

    const finalRequestDto =
      shellRequestDto || state.filteredPlans.shellRequestDto

    const filters = Object.fromEntries(
      Object.entries(finalRequestDto?.filters || {}).filter(
        ([, value]) =>
          Object.keys(value).length > 1 && hasValidFilterValue(value)
      )
    )

    const request = { ...finalRequestDto, filters }
    const response = (await getFilteredPlans(request, shells))
      ?.data as IFilteredShellsResponse
    const groupedShells = response?.data as IFilteredShellsResponse['data']

    if (groupedShells?.shells) {
      Object.keys(groupedShells.shells).forEach((shellKey) => {
        groupedShells.shells[shellKey] = [
          ...groupedShells.shells[shellKey],
        ].sort((a, b) => compareElevations(a.shellElevation, b.shellElevation))
      })
    }

    return response
  }
)

export const fetchPlanDetails = createAsyncThunk(
  'filteredPlans/fetchPlanDetails',
  async (params: GetPlanDetailsParams) => {
    const { basePlanId, division, groupBy, plan, planCategory, submitted } =
      params

    const requestFilters: IFilteredPlansRequest = {
      currentPage: 1,
      filters: {
        basePlan: {
          operation: FilterOperationType.EQUALS,
          value: params.basePlanId,
        },
      },
      pageSize: MAX_ELEVATIONS_PER_BASE_PLAN,
      submitted: params.submitted,
    }

    const response = (
      await getPlanDetails({
        basePlanId,
        division,
        filters: requestFilters,
        groupBy,
        plan,
        planCategory,
        submitted,
      })
    )?.data as IFilteredPlanDetailsResponse

    if (response?.plans) {
      response.plans = [...response.plans].sort((a: IPlan, b: IPlan) =>
        compareElevations(a.planElevation, b.planElevation)
      )
    }

    return response
  }
)

export const fetchUnitDetails = createAsyncThunk(
  'filteredPlans/fetchUnitDetails',
  async (params: GetMultiFamilyDetailsParams) => {
    const { basePlanId, classType, division, groupBy, plan, planCategory } =
      params
    const response = (
      await getMultiFamilyDetails({
        basePlanId,
        classType,
        division,
        groupBy,
        plan,
        planCategory,
      })
    )?.data as IFilteredUnitDetailsResponse

    if (response?.plans) {
      response.plans = [...response.plans].sort((a: IUnit, b: IUnit) =>
        compareElevations(a.unitElevation, b.unitElevation)
      )
    }

    return response
  }
)

export const fetchShellDetails = createAsyncThunk(
  'filteredPlans/fetchShellDetails',
  async (params: GetMultiFamilyDetailsParams) => {
    const { basePlanId, classType, division, groupBy, plan, planCategory } =
      params
    const response = (
      await getMultiFamilyDetails({
        basePlanId,
        classType,
        division,
        groupBy,
        plan,
        planCategory,
      })
    )?.data as IFilteredShellDetailsResponse

    if (response?.plans) {
      response.plans = [...response.plans].sort((a: IShell, b: IShell) =>
        compareElevations(a.shellElevation, b.shellElevation)
      )
    }

    return response
  }
)

export const filteredPlansSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchFilteredPlans.pending, (state) => {
        state.error = null
        state.loading = true
      })
      .addCase(fetchFilteredPlans.fulfilled, (state, action) => {
        state.plans = action.payload
        state.error = null
        state.loading = false
      })
      .addCase(fetchFilteredPlans.rejected, (state, action) => {
        // Request was cancelled because a newer one superseded it; keep the
        // loading state for the in-flight request and don't surface an error.
        if (action.error.name === 'CanceledError') {
          return
        }
        state.error = action.error.message ?? 'Error fetching filtered plans'
        state.loading = false
      })
      .addCase(fetchFilteredUnits.pending, (state) => {
        state.error = null
        state.loading = true
      })
      .addCase(fetchFilteredUnits.fulfilled, (state, action) => {
        state.units = action.payload
        state.error = null
        state.loading = false
      })
      .addCase(fetchFilteredUnits.rejected, (state, action) => {
        // Request was cancelled because a newer one superseded it; keep the
        // loading state for the in-flight request and don't surface an error.
        if (action.error.name === 'CanceledError') {
          return
        }
        state.error = action.error.message ?? 'Error fetching filtered units'
        state.loading = false
      })
      .addCase(fetchFilteredShells.pending, (state) => {
        state.error = null
        state.loading = true
      })
      .addCase(fetchFilteredShells.fulfilled, (state, action) => {
        state.shells = action.payload
        state.error = null
        state.loading = false
      })
      .addCase(fetchFilteredShells.rejected, (state, action) => {
        // Request was cancelled because a newer one superseded it; keep the
        // loading state for the in-flight request and don't surface an error.
        if (action.error.name === 'CanceledError') {
          return
        }
        state.error = action.error.message ?? 'Error fetching filtered shells'
        state.loading = false
      })
      .addCase(fetchPlanDetails.pending, (state) => {
        state.error = null
        state.loading = true
      })
      .addCase(fetchPlanDetails.fulfilled, (state, action) => {
        state.plan = action.payload
        state.error = null
        state.loading = false
      })
      .addCase(fetchPlanDetails.rejected, (state, action) => {
        state.error = action.error.message ?? 'Error fetching plan details'
        state.loading = false
      })
      .addCase(fetchUnitDetails.pending, (state) => {
        state.error = null
        state.loading = true
      })
      .addCase(fetchUnitDetails.fulfilled, (state, action) => {
        state.unit = action.payload
        state.error = null
        state.loading = false
      })
      .addCase(fetchUnitDetails.rejected, (state, action) => {
        state.error = action.error.message ?? 'Error fetching plan details'
        state.loading = false
      })
      .addCase(fetchShellDetails.pending, (state) => {
        state.error = null
        state.loading = true
      })
      .addCase(fetchShellDetails.fulfilled, (state, action) => {
        state.shell = action.payload
        state.error = null
        state.loading = false
      })
      .addCase(fetchShellDetails.rejected, (state, action) => {
        state.error = action.error.message ?? 'Error fetching plan details'
        state.loading = false
      })
  },
  initialState,
  name: 'filteredPlansSlice',
  reducers: {
    clearAllComparisonAttributes: (
      state,
      action: { payload: { classType: classTypes } }
    ) => {
      const { classType } = action.payload
      state.comparisonAttributes.forEach((attr) => {
        if (attr.appliedTo.includes(classType) && !attr.isRequired) {
          attr.enabled = false
        }
      })
    },
    enabledAllFilters: (
      state,
      action: { payload: { classType: classTypes } }
    ) => {
      state.filters.forEach((filter) => {
        if (filter.appliedTo.includes(action.payload.classType)) {
          filter.enabled = true
        }
      })
      saveVisibleFilters(action.payload.classType, state.filters)
    },
    initializeComparisonAttributesForClassType: (
      state,
      action: { payload: { classType: classTypes } }
    ) => {
      const { classType } = action.payload
      state.comparisonAttributes = loadComparisonAttributes(classType)
    },
    initializeFiltersForClassType: (
      state,
      action: { payload: { classType: classTypes; familyType: familyTypes } }
    ) => {
      const { classType, familyType } = action.payload
      // Load visible filters from localStorage
      const visibleFilters = loadVisibleFilters(classType, filterMenuFields)
      state.filters = visibleFilters

      // Load filter values from sessionStorage
      const storedFilterValues = loadFilterValues(classType)
      if (classType === classTypes.SINGLE) {
        state.planRequestDto = {
          ...state.planRequestDto,
          classTypes: classTypes.SINGLE,
          familyType,
          filters: {
            ...state.planRequestDto.filters,
            ...(storedFilterValues
              ? (storedFilterValues as IEnhancedPlanFilterCriteria)
              : {}),
          },
        }
      } else if (classType === classTypes.SHELL) {
        state.shellRequestDto = {
          ...state.shellRequestDto,
          classTypes: classTypes.SHELL,
          familyType,
          filters: {
            ...state.shellRequestDto.filters,
            ...(storedFilterValues
              ? (storedFilterValues as IEnhancedShellFilterCriteria)
              : {}),
          },
        }
      } else if (classType === classTypes.UNIT) {
        state.unitRequestDto = {
          ...state.unitRequestDto,
          classTypes: classTypes.UNIT,
          familyType,
          filters: {
            ...state.unitRequestDto.filters,
            ...(storedFilterValues
              ? (storedFilterValues as IEnhancedUnitFilterCriteria)
              : {}),
          },
        }
      }
    },
    resetFilteredPlans: (state) => {
      state.plans = {} as IFilteredPlansResponse
      state.planRequestDto = {
        currentPage: 1,
        pageSize: 10,
        submitted: true,
      } as IFilteredPlansRequest
      state.error = null
      state.loading = false
    },
    resetFilters: (state, action: { payload: { classType: classTypes } }) => {
      state.filters.forEach((filter) => {
        filter.enabled = false
      })
      saveVisibleFilters(action.payload.classType, state.filters)
    },
    restoreExportsFiltersInitialState: (state) => {
      state.exportFilters = cloneDeep(state.initialExportFilters)
    },
    restoreInitialState: (state) => {
      state.filters = cloneDeep(state.initialFilters)
    },
    restorePreviousFilters: (
      state,
      action: { payload: { classType: classTypes } }
    ) => {
      state.filters = cloneDeep(state.previousFilters)
      saveVisibleFilters(action.payload.classType, state.filters)
    },
    saveComparisonAttributesToStorage: (
      state,
      action: { payload: { classType: classTypes } }
    ) => {
      const { classType } = action.payload
      saveComparisonAttributes(classType, state.comparisonAttributes)
    },
    saveInitialState: (state) => {
      state.initialFilters = cloneDeep(state.filters)
    },
    selectAllComparisonAttributes: (
      state,
      action: { payload: { classType: classTypes } }
    ) => {
      const { classType } = action.payload
      state.comparisonAttributes.forEach((attr) => {
        if (attr.appliedTo.includes(classType)) {
          attr.enabled = true
        }
      })
    },
    setPlanRequestPaginationParams: (
      state,
      action: { payload: { pageSize?: number; currentPage?: number } }
    ) => {
      if (action.payload.pageSize !== undefined) {
        state.planRequestDto.pageSize = action.payload.pageSize
      }
      if (action.payload.currentPage !== undefined) {
        state.planRequestDto.currentPage = action.payload.currentPage
      }
    },
    setShellRequestPaginationParams: (
      state,
      action: { payload: { pageSize?: number; currentPage?: number } }
    ) => {
      if (action.payload.pageSize !== undefined) {
        state.shellRequestDto.pageSize = action.payload.pageSize
      }
      if (action.payload.currentPage !== undefined) {
        state.shellRequestDto.currentPage = action.payload.currentPage
      }
    },
    setSortBy: (
      state,
      action: { payload: { classType?: classTypes; sortBy?: sortFieldsEnum } }
    ) => {
      switch (action.payload.classType) {
        case classTypes.SINGLE:
          state.planRequestDto.sortBy = action.payload.sortBy
          break
        case classTypes.SHELL:
          state.shellRequestDto.sortBy = action.payload.sortBy
          break
        case classTypes.UNIT:
          state.unitRequestDto.sortBy = action.payload.sortBy
          break
      }
    },
    setSortOrder: (
      state,
      action: { payload: { classType?: classTypes; sortOrder?: sortOrderEnum } }
    ) => {
      switch (action.payload.classType) {
        case classTypes.SINGLE:
          state.planRequestDto.sortOrder = action.payload.sortOrder
          break
        case classTypes.SHELL:
          state.shellRequestDto.sortOrder = action.payload.sortOrder
          break
        case classTypes.UNIT:
          state.unitRequestDto.sortOrder = action.payload.sortOrder
          break
      }
    },
    setUnitRequestPaginationParams: (
      state,
      action: { payload: { pageSize?: number; currentPage?: number } }
    ) => {
      if (action.payload.pageSize !== undefined) {
        state.unitRequestDto.pageSize = action.payload.pageSize
      }
      if (action.payload.currentPage !== undefined) {
        state.unitRequestDto.currentPage = action.payload.currentPage
      }
    },
    toggleComparisonAttribute: (
      state,
      action: {
        payload: { enabled: boolean; id: string; classType: classTypes }
      }
    ) => {
      const { enabled, id } = action.payload
      const attribute = state.comparisonAttributes.find(
        (attr) => attr.id === id
      )
      if (attribute && !attribute.isRequired) {
        attribute.enabled = enabled
      }
    },
    toggleExportsFilter: (
      state,
      action: { payload: { id: string; enabled: boolean } }
    ) => {
      const filter = state.exportFilters.find((f) => f.id === action.payload.id)
      if (filter) {
        filter.enabled = action.payload.enabled
      }
    },
    toggleFilter: (
      state,
      action: {
        payload: { id: string; enabled: boolean; classType: classTypes }
      }
    ) => {
      const filter = state.filters.find((f) => f.id === action.payload.id)
      if (filter) {
        filter.enabled = action.payload.enabled
        saveVisibleFilters(action.payload.classType, state.filters)
      }
    },
    updateFilteredPlansRequest: (
      state,
      action: { payload: Partial<IFilteredPlansRequest> }
    ) => {
      state.planRequestDto = { ...state.planRequestDto, ...action.payload }
      saveFilterValues(
        classTypes.SINGLE,
        state.planRequestDto.filters as IEnhancedPlanFilterCriteria
      )
    },
    updateFilteredShellsRequest: (
      state,
      action: { payload: Partial<IFilteredShellsRequest> }
    ) => {
      state.shellRequestDto = { ...state.shellRequestDto, ...action.payload }
      saveFilterValues(
        classTypes.SHELL,
        state.shellRequestDto.filters as IEnhancedShellFilterCriteria
      )
    },
    updateFilteredUnitsRequest: (
      state,
      action: { payload: Partial<IFilteredUnitsRequest> }
    ) => {
      state.unitRequestDto = { ...state.unitRequestDto, ...action.payload }
      saveFilterValues(
        classTypes.UNIT,
        state.unitRequestDto.filters as IEnhancedUnitFilterCriteria
      )
    },
    updatePreviousFilters: (state) => {
      state.previousFilters = cloneDeep(state.filters)
    },
  },
})

export const {
  clearAllComparisonAttributes,
  enabledAllFilters,
  initializeComparisonAttributesForClassType,
  initializeFiltersForClassType,
  resetFilteredPlans,
  resetFilters,
  restoreExportsFiltersInitialState,
  restoreInitialState,
  restorePreviousFilters,
  saveComparisonAttributesToStorage,
  saveInitialState,
  selectAllComparisonAttributes,
  setPlanRequestPaginationParams,
  setShellRequestPaginationParams,
  setUnitRequestPaginationParams,
  toggleComparisonAttribute,
  toggleExportsFilter,
  toggleFilter,
  updateFilteredPlansRequest,
  updateFilteredShellsRequest,
  updateFilteredUnitsRequest,
  updatePreviousFilters,
} = filteredPlansSlice.actions
